import { useEffect, useId, useState } from "react";

/**
 * Mermaid takes colours as strings and cannot resolve CSS variables itself, so
 * the Bifrost tokens are read off the live computed style.
 */
function readThemeVariables() {
  const style = getComputedStyle(document.body);
  const token = (name: string, fallback: string) =>
    style.getPropertyValue(name).trim() || fallback;

  return {
    background: token("--bfc-base-3", "#ffffff"),
    primaryColor: token("--bfc-base-2", "#f2f2f2"),
    primaryTextColor: token("--bfc-base-c-1", "#000000"),
    primaryBorderColor: token("--bfc-base-c-dimmed", "#cccccc"),
    lineColor: token("--bfc-base-c-2", "#666666"),
    textColor: token("--bfc-base-c-1", "#000000"),
    noteBkgColor: token("--bfc-base-2", "#f2f2f2"),
    noteTextColor: token("--bfc-base-c-1", "#000000"),
    noteBorderColor: token("--bfc-base-c-dimmed", "#cccccc"),
    fontFamily: token("--bff-font-family", "inherit"),
  };
}

/**
 * The Bifrost theme tokens, re-read whenever the colour mode changes. Bifrost
 * toggles `bf-lightmode` / `bf-darkmode` on the document element and, in
 * "system" mode, sets neither and defers to `prefers-color-scheme` — so
 * following the mode means watching both.
 */
function useThemeVariables() {
  const [variables, setVariables] = useState(readThemeVariables);

  useEffect(() => {
    const refresh = () => setVariables(readThemeVariables());

    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", refresh);

    return () => {
      observer.disconnect();
      media.removeEventListener("change", refresh);
    };
  }, []);

  return variables;
}

/**
 * Renders a ```mermaid fenced block as a diagram.
 *
 * Mermaid is imported lazily so its weight lands only on the documentation
 * pages that actually contain a diagram, not in the main bundle. Themed off the
 * Bifrost tokens, and re-rendered when the colour mode changes, so a diagram
 * follows light/dark mode with the rest of the page.
 */
export function MermaidDiagram({ source }: { source: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const themeVariables = useThemeVariables();
  // Mermaid needs a DOM-id-safe, unique handle per diagram on the page, and it
  // also puts the id into generated CSS selectors, so strip anything that is not
  // safe in both rather than only the React 18 colon delimiters.
  const id = `mermaid-${useId().replace(/[^a-zA-Z0-9-]/g, "")}`;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const mermaid = (await import("mermaid")).default;

        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "base",
          fontFamily: themeVariables.fontFamily,
          themeVariables,
        });

        const { svg } = await mermaid.render(id, source);
        if (!cancelled) setSvg(svg);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source, id, themeVariables]);

  // Fall back to the diagram source rather than an empty box, so a render
  // failure still leaves the reader something they can follow.
  if (failed) {
    return (
      <pre className="md-pre">
        <code>{source}</code>
      </pre>
    );
  }

  return (
    <div
      className="md-mermaid"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: mermaid renders to an SVG string and there is no other way to mount it. The input is repo-authored markdown, never user input, and mermaid runs with securityLevel "strict", which sanitises the output.
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
    />
  );
}
